import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { makeStyles } from '@material-ui/core'
import PDF from 'pdf'
import { Button, Icon, Typography } from 'foundations-library/components'
import Dialog from '../Dialog/Dialog'

const DEFAULT_DOCUMENT_TITLE = 'Sample document'

const jss = () => ({
    button: {
        marginLeft: 10
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 20,
        paddingRight: 56
    },
    title: {
        flex: 1,
        minWidth: 0
    },
    titleContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0
    },
    titleIcon: {
        flexShrink: 0
    },
    titleText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    downloadButton: {
        flexShrink: 0
    },
    preview: {
        width: '100%',
        height: '70vh',
        minHeight: 480,
        overflow: 'hidden'
    },
    message: {
        padding: '32px 0'
    },
    dialog: {
        '& .dialog-container': {
            paddingTop: 25
        }
    }
})

const useStyles = makeStyles(jss)

interface PropsInterface {
    url: string
}

const getFileNameFromUrl = (url: string, fallback = 'sample-document.pdf') => {
    if (!url) return fallback

    try {
        const parsedUrl = new URL(url)
        const fileName = parsedUrl.pathname.split('/').pop()

        return decodeURIComponent(fileName || fallback)
    } catch (error) {
        const fileName = url.split('/').pop()?.split('?')[0]

        return decodeURIComponent(fileName || fallback)
    }
}

const getFileNameFromContentDisposition = (contentDisposition?: string) => {
    if (!contentDisposition) return null

    const utfFileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)

    if (utfFileNameMatch?.[1]) {
        return decodeURIComponent(utfFileNameMatch[1].trim().replace(/^["']|["']$/g, ''))
    }

    const simpleFileNameMatch = contentDisposition.match(/filename\s*=\s*["']?([^;"']+)["']?/i)

    return simpleFileNameMatch?.[1] ? decodeURIComponent(simpleFileNameMatch[1].trim()) : null
}

const getDocumentFileName = (url: string, headers?: Record<string, string>) => {
    const contentDisposition = headers?.['content-disposition'] || headers?.['Content-Disposition']

    return getFileNameFromContentDisposition(contentDisposition) || getFileNameFromUrl(url)
}

/**
 *
 */
const SampleDocument = ({ url }: PropsInterface): JSX.Element => {
    if (!url) return <></>

    const styles = useStyles()
    const objectUrlRef = useRef<string | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const [documentFileName, setDocumentFileName] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const downloadDocument = () => {
        if (!blobUrl) return

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = documentFileName || getFileNameFromUrl(url)
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    useEffect(() => {
        let isActive = true

        const loadDocument = async () => {
            setIsLoading(true)

            if (!url) {
                setBlobUrl(null)
                setDocumentFileName(null)
                setIsLoading(false)
                return
            }

            try {
                const response = await axios.get(url, { responseType: 'blob' })

                if (!isActive) return

                const nextObjectUrl = window.URL.createObjectURL(response.data)

                if (objectUrlRef.current) {
                    window.URL.revokeObjectURL(objectUrlRef.current)
                }

                objectUrlRef.current = nextObjectUrl
                setBlobUrl(nextObjectUrl)
                setDocumentFileName(getDocumentFileName(url, response.headers))
            } catch (error) {
                if (!isActive) return

                setBlobUrl(null)
                setDocumentFileName(null)
            } finally {
                if (isActive) {
                    setIsLoading(false)
                }
            }
        }

        loadDocument()

        return () => {
            isActive = false

            if (objectUrlRef.current) {
                window.URL.revokeObjectURL(objectUrlRef.current)
                objectUrlRef.current = null
            }
        }
    }, [url])

    return <>
        <Button
            type='button'
            variant='outlined'
            icon='document'
            iconLeft
            className={styles.button}
            onClick={() => setIsDialogOpen(true)}
            data-testid='sample-document-button'
        >
            See a sample document
        </Button>
        <Dialog
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            width={960}
            className={styles.dialog}
            data-testid='sample-document-dialog'
        >
            <div className={styles.header} data-testid='sample-document-header'>
                <div className={styles.title} data-testid='sample-document-title-container'>
                    <div className={styles.titleContent}>
                        <Icon
                            name='document'
                            size={20}
                            color='neutral-700'
                            className={styles.titleIcon}
                            data-testid='sample-document-title-icon'
                        />
                        <Typography
                            variant='h6'
                            color='neutral-900'
                            className={styles.titleText}
                            data-testid='sample-document-title'
                        >
                            {(documentFileName || DEFAULT_DOCUMENT_TITLE).replaceAll('+', ' ')}
                        </Typography>
                    </div>
                </div>
                {blobUrl && <Button
                    type='button'
                    variant='text'
                    color='neutral'
                    icon='download'
                    iconLeft
                    className={styles.downloadButton}
                    onClick={downloadDocument}
                    data-testid='sample-document-download-button'
                >
                    Download
                </Button>}
            </div>
            {blobUrl && <div className={styles.preview} data-testid='sample-document-preview'>
                <PDF url={blobUrl} />
            </div>}
            {!blobUrl && <Typography
                variant='body1'
                color='neutral-700'
                className={styles.message}
                data-testid='sample-document-message'
            >
                {isLoading ? 'Loading sample document...' : 'Sample document unavailable.'}
            </Typography>}
        </Dialog>
    </>
}

export default SampleDocument
